/** Pure formatting helpers shared across multiple tab components. */

/**
 * Local-timezone YYYY-MM-DD.
 * `new Date().toISOString().slice(0,10)` is UTC and rolls over to the next day
 * late in the evening in Europe/Berlin, which mismatched the backend's local day
 * keys (e.g. a pickup dated "today" got filtered out while still labelled HEUTE).
 */
export function todayLocalISO(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function formatW(w: number, digits = 2): string {
  return Math.abs(w) >= 1000
    ? (w / 1000).toFixed(digits) + ' kW'
    : w.toFixed(0) + ' W';
}

/** Relative "time since" label (e.g. "5s", "12min", "3h"). `now` is passed in so
 *  callers can drive it from a shared nowTick without per-instance clocks. */
export function updatedAgo(ts: number | null, now: number): string {
  if (ts === null) return '';
  const s = Math.floor((now - ts) / 1000);
  if (s < 60)   return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  return `${Math.floor(s / 3600)}h`;
}

export function formatKwh(kwh: number): string {
  return (kwh || 0).toFixed(2) + ' kWh';
}

export function fmtDate(dateStr: string, lang: string): string {
  if (!dateStr || dateStr === '?') return dateStr;
  // Anchor to local midnight so a YYYY-MM-DD never renders the wrong weekday.
  const d = new Date(dateStr + 'T00:00:00');
  const locale = lang === 'de' ? 'de-DE' : 'en-GB';
  return d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: '2-digit' });
}

export function autarkieColor(pct: number): string {
  if (pct >= 80) return 'text-success';
  if (pct >= 50) return 'text-warning';
  return 'text-danger';
}

/** Clamp a percentage to [0, 100] and strip NaN/−0. */
export function clampAutarkie(pct: number): number {
  return Math.max(0, Math.min(100, pct || 0));
}

// ── Bootstrap colour-class helpers (shared threshold scales) ────────────────
export function tempColor(tempC: number): string {
  if (tempC >= 28) return 'text-danger';
  if (tempC >= 22) return 'text-warning';
  if (tempC >= 16) return 'text-success';
  return 'text-info';
}

/** Asymmetric on purpose: high humidity (mould risk) escalates to danger; the
 *  very-dry end tops out at warning. */
export function humidityColor(hum: number): string {
  if (hum > 70) return 'text-danger';
  if (hum > 60) return 'text-warning';
  if (hum >= 30) return 'text-success';
  return 'text-warning';
}

export function signalClass(dBm: number): string {
  if (dBm >= -55) return 'text-success';
  if (dBm >= -70) return 'text-warning';
  return 'text-danger';
}

export function fmtMonth(ym: string, lang: string): string {
  const d = new Date(ym + '-01');
  const locale = lang === 'de' ? 'de-DE' : 'en-GB';
  return d.toLocaleDateString(locale, { month: 'long' });
}

export function fmtMonthShort(ym: string, lang: string): string {
  const d = new Date(ym + '-01');
  const locale = lang === 'de' ? 'de-DE' : 'en-GB';
  return d.toLocaleDateString(locale, { month: 'short' });
}

function localeFor(lang: string): string {
  return lang === 'de' ? 'de-DE' : 'en-GB';
}

/** "September 2025" from a "YYYY-MM" key. */
export function fmtMonthYear(ym: string, lang: string): string {
  if (!ym) return ym;
  return new Date(ym + '-01').toLocaleDateString(localeFor(lang), { month: 'long', year: 'numeric' });
}

/** "01.09.2025" from a "YYYY-MM-DD" string (anchored to local midnight). */
export function fmtDayMonthYear(dateStr: string, lang: string): string {
  if (!dateStr) return dateStr;
  return new Date(dateStr).toLocaleDateString(localeFor(lang), { day: '2-digit', month: '2-digit', year: 'numeric' });
}
